/**
 * The four primary visibility switches of Calendar and Timeline (tasks, events, completed,
 * overdue) and their per-scope persistence.
 *
 * A scope is one board's Calendar, one board's Timeline, or one workspace's Calendar: each has
 * its own stored preference, so switching boards never leaks a filter into another one. The
 * preference lives in localStorage (per device) and every read/write is guarded: a blocked or
 * full storage just means "defaults, not remembered". Filtering only hides items in the view.
 */
import { useCallback, useState } from 'react';

export interface VisibilityPrefs {
  showTasks: boolean;
  showEvents: boolean;
  showCompleted: boolean;
  showOverdue: boolean;
}

export const DEFAULT_VISIBILITY: VisibilityPrefs = {
  showTasks: true,
  showEvents: true,
  showCompleted: true,
  showOverdue: true,
};

const KEYS = ['showTasks', 'showEvents', 'showCompleted', 'showOverdue'] as const;
const PREFIX = 'zadok.visibility.';

export type VisibilityScope =
  | { view: 'calendar' | 'timeline'; kind: 'board'; id: string }
  | { view: 'calendar'; kind: 'workspace' | 'me' | 'all'; id: string };

export function scopeKey(scope: VisibilityScope): string {
  return `${PREFIX}${scope.view}.${scope.kind}.${scope.id}`;
}

export function readVisibility(key: string): VisibilityPrefs {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return DEFAULT_VISIBILITY;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_VISIBILITY;
    const record = parsed as Record<string, unknown>;
    const prefs = { ...DEFAULT_VISIBILITY };
    for (const name of KEYS) {
      if (typeof record[name] === 'boolean') prefs[name] = record[name];
    }
    return prefs;
  } catch {
    return DEFAULT_VISIBILITY;
  }
}

export function writeVisibility(key: string, prefs: VisibilityPrefs): void {
  try {
    if (isDefaultVisibility(prefs)) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Private mode / quota: the choice just is not remembered.
  }
}

export function isDefaultVisibility(prefs: VisibilityPrefs): boolean {
  return KEYS.every((name) => prefs[name]);
}

/** How many of the four switches are off (the number shown on the Filters button). */
export function hiddenCategories(prefs: VisibilityPrefs): number {
  return KEYS.filter((name) => !prefs[name]).length;
}

/**
 * Stored visibility of one scope. The value is read again whenever the scope changes, so two
 * boards never share state; setting persists at once.
 */
export function useStoredVisibility(
  key: string,
): [VisibilityPrefs, (patch: Partial<VisibilityPrefs>) => void, () => void] {
  const [state, setState] = useState(() => ({ key, prefs: readVisibility(key) }));
  const prefs = state.key === key ? state.prefs : readVisibility(key);
  const set = useCallback(
    (patch: Partial<VisibilityPrefs>) => {
      const next = { ...(state.key === key ? state.prefs : readVisibility(key)), ...patch };
      writeVisibility(key, next);
      setState({ key, prefs: next });
    },
    [key, state],
  );
  const reset = useCallback(() => {
    writeVisibility(key, DEFAULT_VISIBILITY);
    setState({ key, prefs: DEFAULT_VISIBILITY });
  }, [key]);
  return [prefs, set, reset];
}
